import { trackEvent, isHmBrand, consoleEvents, trackViewEvent } from '@pb/utils'
import { Dispatch } from 'redux'

import appSelectors from 'App/duck/selectors'
import pLimit from 'helpers/pLimit'
import { setABTestingData } from 'helpers/tracking'
import updateMiniCart from 'helpers/updateMiniCart'
import { ThunkResult } from 'redux/types'

import { FAVOURITES_ERROR_GROUPS, FAVOURITES_ERROR_DISPLAY_MODE } from '../constants'
import {
  fetchFavourites,
  deleteFavouriteItem,
  moveToCartFromFavourites,
  updateVariant,
  fetchStoreSearchResults,
} from '../requests'
import { EVENT_TYPES, getFavouritesEventTracking, getFavouritesTracking } from '../tracking'
import actions from './actions'
import { normalizeFavouritesResponse, normalizeHiddenFavouritesResponse } from './normalize'
import { getFavouritesItems } from './selectors'
import { AddToBagVerificationData, FavouritesState, StoreSearchQuery } from './types'

const limit = pLimit(1)

const getABTestData = (state): any => ({
  items: getFavouritesItems(state),
  userId: appSelectors.getUserId(state),
})

export const operationFetchFavourites =
  (shouldSendTrackingOnLoad?: boolean): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(actions.fetchFavouritesStart())

    const isUserStatus = appSelectors.getUserStatus(getState()) === 'FULL_MEMBER'

    try {
      const favouritesResponse = await fetchFavourites()
      const favouritesData = normalizeFavouritesResponse(favouritesResponse, isUserStatus)
      const hiddenfavouritesData = normalizeHiddenFavouritesResponse(favouritesResponse)

      dispatch(actions.fetchFavouritesSuccess(favouritesData))
      dispatch(actions.fetchHiddenFavouritesSuccess(hiddenfavouritesData))
      if (shouldSendTrackingOnLoad) {
        trackViewEvent(
          getFavouritesTracking(getState(), EVENT_TYPES.favourites_view),
          true,
          isHmBrand,
        )
      }
      setABTestingData(getABTestData(getState()))
    } catch (e) {
      dispatch(actions.fetchFavouritesFail(e))
      dispatch(actions.fetchHiddenFavouritesFail(e))
      if (shouldSendTrackingOnLoad) {
        trackViewEvent(
          getFavouritesTracking(getState(), EVENT_TYPES.favourites_view),
          true,
          isHmBrand,
        )
      }
    }
  }

export const operationDeleteFavouritesItem =
  (productCode: string): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState()
    const previousFavouritesItemsState: FavouritesState['items'] = state.favourites.items
    const previousHiddenFavouritesItemsState: FavouritesState['hiddenItems'] =
      state.favourites.hiddenItems

    dispatch(actions.deleteFavouritesItemStart(productCode))

    try {
      await limit(() => deleteFavouriteItem(productCode))

      trackEvent(
        getFavouritesEventTracking(
          productCode,
          state,
          EVENT_TYPES.remove_from_favourites,
          'unsave',
        ),
        true,
        isHmBrand,
      )

      dispatch(actions.deleteFavouritesItemSuccess())
      dispatch(actions.deleteHiddenFavouritesItem(productCode))
    } catch (e) {
      dispatch(
        actions.deleteFavouritesItemFail(
          previousFavouritesItemsState,
          previousHiddenFavouritesItemsState,
        ),
      )
      dispatch(
        actions.showError({
          displayMode: FAVOURITES_ERROR_DISPLAY_MODE.RENDER_NOTIFICATION_BAR,
          errorMessage: e.message,
          errorGroup: FAVOURITES_ERROR_GROUPS.REMOVE,
        }),
      )
    }
  }

export const operationMoveToCart =
  (
    variantCode: string,
    productCode: string,
    addToBagVerificationData: AddToBagVerificationData,
  ): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState()
    const previousFavouriteItemsState: FavouritesState['items'] = state.favourites.items

    const previousHiddenFavouriteItemsState: FavouritesState['hiddenItems'] =
      state.favourites.hiddenItems

    dispatch(actions.moveToCartStart(variantCode))
    dispatch(actions.showAddToBagVerificationStart())

    try {
      await limit(() => moveToCartFromFavourites(variantCode, productCode))

      trackEvent(
        getFavouritesEventTracking(variantCode, state, EVENT_TYPES.add_to_bag, 'add'),
        true,
        isHmBrand,
      )

      trackEvent(
        getFavouritesEventTracking(
          productCode,
          state,
          EVENT_TYPES.remove_from_favourites,
          'unsave',
        ),
        true,
        isHmBrand,
      )

      dispatch(actions.moveToCartSuccess())
      dispatch(actions.deleteHiddenFavouritesItem(variantCode))
      dispatch(actions.showAddToBagVerificationSuccess(addToBagVerificationData))

      updateMiniCart()
    } catch (error) {
      dispatch(
        actions.moveToCartFail(previousFavouriteItemsState, previousHiddenFavouriteItemsState),
      )
      dispatch(actions.showAddToBagVerificationError(addToBagVerificationData, error))
    }
  }

export const operationUpdateVariant =
  (newVariantCode: string, productCode: string): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState()
    const previousFavouritesItemsState: FavouritesState['items'] = state.favourites.items
    const previousHiddenFavouritesItemsState: FavouritesState['hiddenItems'] =
      state.favourites.hiddenItems

    const findSameVariant = previousFavouritesItemsState.filter(
      ({ code }: { code: string }) => code === newVariantCode,
    )
    if (newVariantCode !== productCode && findSameVariant.length > 0) {
      dispatch(
        actions.showError({
          displayMode: FAVOURITES_ERROR_DISPLAY_MODE.RENDER_MODAL,
          errorMessage: 'error',
          errorGroup: FAVOURITES_ERROR_GROUPS.SAME_VARIANT,
          variantCode: productCode,
        }),
      )
    } else if (newVariantCode !== productCode) {
      dispatch(actions.updateVariantStart())

      try {
        await limit(() => updateVariant(newVariantCode, productCode))
        dispatch(actions.updateVariantSuccess(newVariantCode, productCode))
        dispatch(actions.updateHiddenFavouritesItem(newVariantCode, productCode))
        trackEvent(
          getFavouritesEventTracking(newVariantCode, getState(), EVENT_TYPES.choose_size),
          true,
          isHmBrand,
        )
      } catch (error) {
        dispatch(
          actions.updateVariantFail(
            previousFavouritesItemsState,
            previousHiddenFavouritesItemsState,
          ),
        )
        dispatch(
          actions.showError({
            displayMode: FAVOURITES_ERROR_DISPLAY_MODE.RENDER_NOTIFICATION_BAR,
            errorMessage: error.message,
            errorGroup: FAVOURITES_ERROR_GROUPS.CHANGE_VARIANT,
            variantCode: productCode,
          }),
        )
      }
    }
  }

export const operationFetchStoreSearchResults =
  (searchQuery: StoreSearchQuery): ThunkResult<Promise<void>> =>
  async (dispatch: Dispatch) => {
    dispatch(actions.fetchStoreSearchResultsStart())
    try {
      const response = await limit(() => fetchStoreSearchResults(searchQuery))
      dispatch(actions.fetchStoreSearchResultsSuccess(response))
    } catch (e) {
      dispatch(actions.fetchStoreSearchResultsFail(e))
    }
  }

export const operationResetStoreSearchResults = () => async (dispatch) => {
  dispatch(actions.resetStoreSearchResultsStart())

  try {
    dispatch(actions.resetStoreSearchResultsSuccess())
  } catch (e) {
    dispatch(actions.resetStoreSearchResultsFail(e))
  }
}

export const operationHideAddToBag = () => async (dispatch) => {
  try {
    dispatch(actions.hideAddToBagVerification())
  } catch (e) {
    consoleEvents.error(e)
  }
}

export const operationViewFavouritesEmpty = (): ThunkResult<Promise<void>> => async (_, getState) => {
  const state = getState()
  try {
    trackViewEvent(
      getFavouritesTracking(state, EVENT_TYPES.favourites_view_empty),
      true,
      isHmBrand,
    )
  } catch (e) {
    consoleEvents.error(e)
  }
}